import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Calendar, Clock, MapPin, Briefcase, Navigation, ArrowRight, Bell, ListChecks,
  PlayCircle, CheckCircle2, Camera, AlertTriangle, LogIn, ClipboardList, User,
} from "lucide-react";

/**
 * Employee "My Day"
 *
 * Answers the four questions an installer opens the app to answer:
 *   Where am I going?  What am I doing?  When?  Who with?
 *
 * Layout:
 *   Row 1 · Next Job / Hours This Week / Open Tasks / Notifications
 *   Card  · Today's Schedule (job cards with contextual actions)
 *   Card  · Upcoming days
 */
export default function EmployeeToday() {
  const [schedule, setSchedule] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [notifCount, setNotifCount] = useState(0);
  const [openTasks, setOpenTasks] = useState(null);

  useEffect(() => {
    api.get("/employee/schedule", { params: { days: 7 } }).then((r) => setSchedule(r.data)).catch(() => {});
    api.get("/employee/earnings", { params: { period: "month" } }).then((r) => setEarnings(r.data)).catch(() => {});
    api.get("/notifications", { params: { unread_only: true } })
      .then((r) => setNotifCount(Array.isArray(r.data) ? r.data.length : 0))
      .catch(() => {});
    api.get("/employee/open-tasks").then((r) => setOpenTasks(r.data)).catch(() => setOpenTasks({ total: 0 }));
  }, []);

  const today = schedule?.today_iso;
  const todayWOs = schedule?.buckets?.[today] || [];
  const upcomingDates = schedule
    ? Object.keys(schedule.buckets).filter((d) => d !== "unscheduled" && d > today).sort()
    : [];
  const unscheduled = schedule?.buckets?.unscheduled || [];

  const nextJob = useMemo(() => {
    if (todayWOs.length > 0) return { wo: todayWOs[0], when: "Today" };
    for (const d of upcomingDates) {
      if ((schedule.buckets[d] || []).length > 0) {
        return {
          wo: schedule.buckets[d][0],
          when: new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
        };
      }
    }
    return null;
  }, [todayWOs, upcomingDates, schedule]);

  return (
    <div data-testid="employee-today">
      <p className="overline">Field Ops</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">My Day</h1>
      <p className="text-sm text-[#475569] dark:text-slate-400 mt-1 mb-6">
        {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
      </p>

      {/* Operational KPIs: Next Job / Hours / Tasks / Notifications */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {/* Next Job — the most important thing an installer sees */}
        <Link
          to={nextJob ? `/employee/work-orders?id=${nextJob.wo.work_order_id}` : "/employee/work-orders"}
          data-testid="kpi-next-job"
          className="col-span-2 lg:col-span-2 rounded-2xl p-4 md:p-5 bg-gradient-to-br from-[#0A2540] to-[#103960] text-white shadow-md hover:brightness-110 transition"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-[#C9A356] font-bold">Next job</div>
              {nextJob ? (
                <>
                  <div className="text-lg md:text-xl font-heading font-semibold mt-1 truncate">{nextJob.wo.title}</div>
                  <div className="text-xs text-slate-300 mt-1 truncate">
                    {nextJob.when} · {nextJob.wo.customer_code} · {(nextJob.wo.status || "").replace(/_/g, " ")}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-lg font-heading font-semibold mt-1">No jobs assigned</div>
                  <div className="text-xs text-slate-300 mt-1">Enjoy the quiet day — or check unscheduled work orders.</div>
                </>
              )}
            </div>
            <Briefcase className="w-6 h-6 text-[#C9A356] shrink-0 opacity-90" />
          </div>
        </Link>

        <div className="rounded-2xl p-4 md:p-5 bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 shadow-sm" data-testid="kpi-hours-week">
          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Hours this week</div>
          <div className="text-2xl md:text-3xl font-heading font-semibold text-[#0A2540] dark:text-white mt-1 tabular-nums">
            {schedule?.week_hours ?? 0}<span className="text-lg">h</span>
          </div>
          <div className="text-[11px] text-[#475569] dark:text-slate-400 mt-1">
            Today: {schedule?.today_hours ?? 0}h · Month: {earnings?.total_hours ?? 0}h
          </div>
        </div>

        <Link
          to="/employee/notifications"
          data-testid="kpi-notifications"
          className={`rounded-2xl p-4 md:p-5 shadow-sm transition ${notifCount > 0 ? "bg-[#C9A356] text-[#0A2540]" : "bg-emerald-600 text-white"} hover:brightness-105`}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">Notifications</div>
              <div className="text-2xl md:text-3xl font-heading font-semibold mt-1 tabular-nums">
                {notifCount}
              </div>
              <div className="text-[11px] opacity-80 mt-1">
                {notifCount === 0 ? "All caught up" : notifCount === 1 ? "1 unread" : `${notifCount} unread`}
              </div>
            </div>
            <Bell className="w-6 h-6 shrink-0 opacity-80" />
          </div>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8">
        <StatChip
          icon={ListChecks}
          label="Open tasks"
          value={openTasks?.total ?? "…"}
          sub={openTasks?.today > 0 ? `${openTasks.today} due today` : "Across your work orders"}
          to="/employee/work-orders?filter=open"
          testid="kpi-open-tasks"
        />
        <StatChip
          icon={AlertTriangle}
          label="Punchlist"
          value={openTasks?.punchlist ?? 0}
          sub="Items to close"
          testid="kpi-punchlist"
        />
        <StatChip
          icon={ClipboardList}
          label="Daily logs due"
          value={openTasks?.logs_due ?? 0}
          sub="Submit before EOD"
          to="/employee/daily-log"
          testid="kpi-logs-due"
        />
        <StatChip
          icon={User}
          label="My performance"
          value=""
          sub="View rating & tips"
          to="/employee/profile"
          testid="kpi-performance"
        />
      </div>

      {/* Today's Schedule */}
      <div className="card-pb p-6 mb-6" data-testid="todays-schedule">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl text-[#0A2540] dark:text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#C9A356]" /> Today's Schedule
          </h2>
          {todayWOs.length > 0 && (
            <span className="text-xs text-[#475569] dark:text-slate-400">
              {todayWOs.length} {todayWOs.length === 1 ? "job" : "jobs"}
            </span>
          )}
        </div>
        {todayWOs.length === 0 ? (
          <div className="text-center py-8 text-[#475569] dark:text-slate-400">
            <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p>Nothing scheduled for today.</p>
            <p className="text-xs mt-1">Check upcoming days or unscheduled WOs below.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {todayWOs.map((wo) => <JobCard key={wo.work_order_id} wo={wo} />)}
          </div>
        )}
      </div>

      {upcomingDates.length > 0 && (
        <div className="card-pb p-6 mb-6" data-testid="upcoming-days">
          <h2 className="font-heading text-lg text-[#0A2540] dark:text-white mb-3">Upcoming days</h2>
          {upcomingDates.slice(0, 7).map((d) => (
            <div key={d} className="mb-4 last:mb-0">
              <div className="text-xs uppercase tracking-wider font-bold text-[#C9A356] mb-2">
                {new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              </div>
              <div className="space-y-2">
                {schedule.buckets[d].map((wo) => <JobCard key={wo.work_order_id} wo={wo} compact />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {unscheduled.length > 0 && (
        <div className="card-pb p-6" data-testid="unscheduled">
          <h2 className="font-heading text-lg text-[#0A2540] dark:text-white mb-3">Awaiting schedule</h2>
          <div className="space-y-2">
            {unscheduled.map((wo) => <JobCard key={wo.work_order_id} wo={wo} compact />)}
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({ icon: Icon, label, value, sub, to, testid }) {
  const inner = (
    <div className="rounded-xl p-3.5 bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 shadow-sm hover:-translate-y-0.5 transition" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">{label}</div>
        <Icon className="w-3.5 h-3.5 text-[#C9A356]" />
      </div>
      {value !== "" && (
        <div className="text-xl font-heading text-[#0A2540] dark:text-white tabular-nums">{value}</div>
      )}
      <div className={`text-[11px] text-[#475569] dark:text-slate-400 ${value === "" ? "mt-0" : "mt-1"} truncate`}>{sub}</div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

/**
 * Operational job card. Surfaces actions contextually based on WO status:
 *   scheduled / not_started  → Navigate · Check In · Open
 *   in_progress             → Open Checklist · Upload Photo · Report Issue · Complete
 *   done / installed        → Open (view-only)
 */
function JobCard({ wo, compact }) {
  const lastLog = (wo.logs || []).filter((l) => l.lat).pop();
  const address = wo.property_address || wo.address || null;
  const navUrl = lastLog
    ? `https://www.google.com/maps/dir/?api=1&destination=${lastLog.lat},${lastLog.lng}`
    : address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
    : null;

  const status = (wo.status || "").toLowerCase();
  const isInProgress = ["in_progress", "started", "checked_in", "in_process"].includes(status);
  const isScheduled  = ["scheduled", "assigned", "not_started"].includes(status) || (!isInProgress && !["done", "installed", "completed", "closed"].includes(status));
  const isDone       = ["done", "installed", "completed", "closed"].includes(status);

  const woLink = `/employee/work-orders?id=${wo.work_order_id}`;

  return (
    <div
      className={`border border-[#E2E8F0] dark:border-slate-700 rounded-lg ${compact ? "p-3" : "p-4"} bg-white dark:bg-slate-800 hover:border-[#C9A356]/50 transition`}
      data-testid={`job-card-${wo.work_order_id}`}
    >
      {/* Header: title + status */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-[#C9A356] shrink-0" />
            <span className="font-medium text-[#0A2540] dark:text-white text-sm truncate">{wo.title}</span>
          </div>
          <div className="text-[11px] text-[#475569] dark:text-slate-400 mt-1">
            {wo.customer_code} · {wo.customer_name || wo.client_name || ""}
          </div>
        </div>
        <span className={`shrink-0 text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
          isDone ? "bg-emerald-100 text-emerald-700" :
          isInProgress ? "bg-blue-100 text-blue-700" :
          "bg-slate-100 text-slate-700"
        }`}>
          {status.replace(/_/g, " ") || "assigned"}
        </span>
      </div>

      {/* Meta row: address · scheduled window · role */}
      {!compact && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px] text-[#475569] dark:text-slate-400 mb-3">
          {address && (
            <div className="flex items-start gap-1.5 min-w-0">
              <MapPin className="w-3.5 h-3.5 text-[#94A3B8] shrink-0 mt-0.5" />
              <span className="truncate" title={address}>{address}</span>
            </div>
          )}
          {wo.scheduled_window && (
            <div className="flex items-start gap-1.5">
              <Clock className="w-3.5 h-3.5 text-[#94A3B8] shrink-0 mt-0.5" />
              <span>{wo.scheduled_window}</span>
            </div>
          )}
          {(wo.role || wo.crew_role) && (
            <div className="flex items-start gap-1.5">
              <User className="w-3.5 h-3.5 text-[#94A3B8] shrink-0 mt-0.5" />
              <span className="capitalize">{wo.role || wo.crew_role}</span>
            </div>
          )}
        </div>
      )}

      {wo.crew_notes && !compact && (
        <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded px-2 py-1.5 mb-3 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{wo.crew_notes}</span>
        </div>
      )}

      {/* Contextual action row */}
      <div className="flex flex-wrap gap-1.5">
        {navUrl && (
          <a
            href={navUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`nav-${wo.work_order_id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium"
          >
            <Navigation className="w-3 h-3" /> Navigate
          </a>
        )}
        {isScheduled && (
          <Link
            to={`${woLink}&action=check-in`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#0A2540] hover:bg-[#123259] text-white rounded font-medium"
            data-testid={`check-in-${wo.work_order_id}`}
          >
            <LogIn className="w-3 h-3" /> Check In
          </Link>
        )}
        {isInProgress && (
          <>
            <Link
              to={`${woLink}&tab=checklist`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-white border border-[#0A2540] text-[#0A2540] dark:bg-slate-900 dark:border-slate-500 dark:text-white rounded hover:bg-slate-50 font-medium"
              data-testid={`checklist-${wo.work_order_id}`}
            >
              <ListChecks className="w-3 h-3" /> Checklist
            </Link>
            <Link
              to={`${woLink}&tab=photos`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-white border border-[#0A2540] text-[#0A2540] dark:bg-slate-900 dark:border-slate-500 dark:text-white rounded hover:bg-slate-50 font-medium"
              data-testid={`photo-${wo.work_order_id}`}
            >
              <Camera className="w-3 h-3" /> Photo
            </Link>
            <Link
              to={`${woLink}&action=complete`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium"
              data-testid={`complete-${wo.work_order_id}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Complete
            </Link>
          </>
        )}
        {isDone && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-emerald-50 text-emerald-700 rounded font-medium">
            <CheckCircle2 className="w-3 h-3" /> Completed
          </span>
        )}
        <Link
          to={woLink}
          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#C9A356] hover:bg-[#DDBD77] text-[#0A2540] rounded font-medium"
          data-testid={`open-${wo.work_order_id}`}
        >
          Open <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
